import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/services/i18n';
import { worksheetAPI, lessonPlanAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Plus, FileText, Settings, LogOut, BookOpen, Upload, Home,
  Library as LibraryIcon, User as UserIcon, BarChart3,
  Calculator, FlaskConical, Globe2, Palette, Music,
  Languages, Sparkles, CheckCircle, ClipboardEdit, Download, Layers, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';
import LessonPlanCard from '@/components/lesson/LessonPlanCard';

const SUBJECT_ICON = (subject = '') => {
  const s = subject.toLowerCase();
  if (s.includes('math')) return { icon: Calculator, bg: 'from-amber-100 to-amber-50', fg: 'text-amber-700' };
  if (s.includes('science') || s.includes('physics') || s.includes('chem') || s.includes('bio'))
    return { icon: FlaskConical, bg: 'from-emerald-100 to-emerald-50', fg: 'text-emerald-700' };
  if (s.includes('social') || s.includes('history') || s.includes('geo') || s.includes('civic'))
    return { icon: Globe2, bg: 'from-sky-100 to-sky-50', fg: 'text-sky-700' };
  if (s.includes('art') || s.includes('draw') || s.includes('craft'))
    return { icon: Palette, bg: 'from-rose-100 to-rose-50', fg: 'text-rose-700' };
  if (s.includes('music')) return { icon: Music, bg: 'from-violet-100 to-violet-50', fg: 'text-violet-700' };
  if (s.includes('hindi') || s.includes('english') || s.includes('language') || s.includes('sanskrit'))
    return { icon: Languages, bg: 'from-indigo-100 to-indigo-50', fg: 'text-indigo-700' };
  return { icon: BookOpen, bg: 'from-stone-100 to-stone-50', fg: 'text-[#C53030]' };
};

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [lessonPlans, setLessonPlans] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const loadLessonPlans = useCallback(async () => {
    try {
      const [plansRes, wsRes] = await Promise.all([
        lessonPlanAPI.getAll(),
        worksheetAPI.getAll().catch(() => ({ data: [] })),
      ]);
      setLessonPlans(plansRes.data);
      setWorksheets(wsRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      toast.error('Failed to load lesson plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLessonPlans();
  }, [loadLessonPlans]);

  const stats = useMemo(() => {
    const total = lessonPlans.length;
    const drafts = lessonPlans.filter((p) => p.is_draft).length;
    const finalized = total - drafts;
    const downloads = lessonPlans.reduce((sum, p) => sum + (p.download_count || 0), 0);
    return { total, drafts, finalized, downloads };
  }, [lessonPlans]);

  const grouped = useMemo(() => {
    const byClass = {};
    lessonPlans.forEach((p) => {
      const key = p.class_name || 'Unassigned';
      if (!byClass[key]) byClass[key] = [];
      byClass[key].push(p);
    });
    return Object.entries(byClass);
  }, [lessonPlans]);

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(docx|pdf)$/i)) {
      toast.error('Please upload a DOCX or PDF file');
      return;
    }
    setImporting(true);
    try {
      const { data } = await lessonPlanAPI.import(file);
      toast.success('Lesson plan imported successfully!');
      navigate(`/builder/${data.lesson_plan_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import lesson plan');
    } finally {
      setImporting(false);
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const handleDelete = async (id) => {
    try {
      await lessonPlanAPI.delete(id);
      setLessonPlans(lessonPlans.filter((p) => p.lesson_plan_id !== id));
      toast.success('Lesson plan deleted');
    } catch { toast.error('Failed to delete lesson plan'); }
  };

  const handleDuplicate = async (id) => {
    try {
      const { data } = await lessonPlanAPI.duplicate(id);
      setLessonPlans([data, ...lessonPlans]);
      toast.success('Lesson plan duplicated');
    } catch { toast.error('Failed to duplicate lesson plan'); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const kpiCards = [
    { label: t('kpiTotal'), value: stats.total, icon: Layers, gradient: 'from-red-500 to-orange-500', testid: 'kpi-total' },
    { label: t('kpiDrafts'), value: stats.drafts, icon: ClipboardEdit, gradient: 'from-amber-500 to-yellow-500', testid: 'kpi-drafts' },
    { label: t('kpiFinalized'), value: stats.finalized, icon: CheckCircle, gradient: 'from-emerald-500 to-teal-500', testid: 'kpi-finalized' },
    { label: t('kpiDownloads'), value: stats.downloads, icon: Download, gradient: 'from-indigo-500 to-purple-500', testid: 'kpi-downloads' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] via-white to-red-50/30">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]">
              <Home className="w-5 h-5 mr-2" /> {t('home')}
            </Button>
            <div className="flex items-center gap-2">
              <BookOpen className="w-8 h-8 text-[#C53030]" />
              <h1 className="text-2xl font-bold text-[#1C1917]">EduRepository</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger data-testid="language-switcher" className="w-24 h-9 border border-stone-200 bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="en">EN</SelectItem>
                <SelectItem value="hi">हिं</SelectItem>
              </SelectContent>
            </Select>
            <Button data-testid="library-nav-button" variant="ghost" onClick={() => navigate('/library')}>
              <LibraryIcon className="w-4 h-4 mr-1" /> {t('library')}
            </Button>
            <Button data-testid="worksheet-library-nav-button" variant="ghost" onClick={() => navigate('/worksheet-library')}>
              <ClipboardList className="w-4 h-4 mr-1" /> Worksheets
            </Button>
            <Button data-testid="question-paper-nav-button" variant="ghost" onClick={() => navigate('/question-paper')} className="text-orange-700 hover:bg-orange-50">
              <FileText className="w-4 h-4 mr-1" /> Question Paper
            </Button>
            {user?.role === 'admin' && (
              <Button data-testid="admin-curriculum-nav-btn" variant="ghost" onClick={() => navigate('/admin/curriculum')} className="text-orange-700 hover:bg-orange-50">
                <Upload className="w-4 h-4 mr-1" /> Admin: Curriculum
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button data-testid="admin-library-nav-btn" variant="ghost" onClick={() => navigate('/admin/library')} className="text-orange-700 hover:bg-orange-50">
                <LibraryIcon className="w-4 h-4 mr-1" /> Admin: Library
              </Button>
            )}
            <Button data-testid="nanno-nav-button" variant="ghost" onClick={() => navigate('/nanno')} className="text-purple-700">
              <Sparkles className="w-4 h-4 mr-1" /> Nanno
            </Button>
            {user?.role === 'admin' && (
              <Button data-testid="analytics-nav-button" variant="ghost" onClick={() => navigate('/analytics')}>
                <BarChart3 className="w-4 h-4 mr-1" /> {t('analytics')}
              </Button>
            )}
            <Button data-testid="profile-nav-button" variant="ghost" onClick={() => navigate('/profile')}>
              <UserIcon className="w-4 h-4 mr-1" /> {t('profile')}
            </Button>
            <span className="text-sm text-stone-600 hidden md:inline">{user?.name}</span>
            <Button data-testid="settings-button" variant="ghost" onClick={() => navigate('/settings')}>
              <Settings className="w-5 h-5" />
            </Button>
            <Button data-testid="logout-button" variant="ghost" onClick={handleLogout}>
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#C53030] mb-2 font-semibold">
              {t('dashboard')}
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1C1917] font-bold">
              {t('yourLessonPlans')}
            </h2>
            <p className="text-base text-stone-600 mt-2">{t('dashboardSubtitle')}</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <input ref={importInputRef} data-testid="import-file-input" type="file" accept=".docx,.pdf" onChange={handleImport} className="hidden" />
            <Button data-testid="import-lesson-plan-button" variant="outline" onClick={() => importInputRef.current?.click()} disabled={importing} className="border border-stone-200">
              <Upload className="w-4 h-4 mr-2" /> {importing ? t('importing') : t('import')}
            </Button>
            <Button data-testid="templates-button" variant="outline" onClick={() => navigate('/templates')} className="border border-stone-200">
              <FileText className="w-4 h-4 mr-2" /> {t('templates')}
            </Button>
            <Button data-testid="generate-worksheet-button" onClick={() => navigate('/worksheet')} className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white rounded-md shadow-md">
              <ClipboardList className="w-4 h-4 mr-2" /> Generate Your Worksheet
            </Button>
            <Button data-testid="generate-qp-button" onClick={() => navigate('/question-paper')} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-md shadow-md">
              <FileText className="w-4 h-4 mr-2" /> New Question Paper
            </Button>
            <Button data-testid="generate-sp-button" onClick={() => navigate('/sample-paper')} className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white rounded-md shadow-md">
              <ClipboardList className="w-4 h-4 mr-2" /> New Sample Paper
            </Button>
            <Button data-testid="new-lesson-plan-button" onClick={() => navigate('/builder')} className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md shadow-md hover:shadow-lg transition-shadow">
              <Sparkles className="w-4 h-4 mr-2" /> {t('newLessonPlan')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {kpiCards.map((k) => (
            <Card key={k.label} data-testid={k.testid} className="p-5 border-0 shadow-md relative overflow-hidden">
              <div className={`absolute inset-0 bg-gradient-to-br ${k.gradient} opacity-10`} />
              <div className="relative">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${k.gradient} flex items-center justify-center mb-3 shadow-sm`}>
                  <k.icon className="w-5 h-5 text-white" />
                </div>
                <p className="text-3xl font-bold text-[#1C1917]">{k.value}</p>
                <p className="text-xs text-stone-500 mt-1">{k.label}</p>
              </div>
            </Card>
          ))}
        </div>

        {!user?.school_id && !user?.school_name && (
          <Card className="p-6 mb-8 border border-[#C53030] bg-red-50">
            <p className="text-[#1C1917] mb-2 font-medium">{t('completeProfileFirst')}</p>
            <Button data-testid="setup-school-button" onClick={() => navigate('/profile')} size="sm" className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md">
              {t('goToProfile')}
            </Button>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C53030] mx-auto mb-4"></div>
            <p className="text-stone-600">Loading...</p>
          </div>
        ) : lessonPlans.length === 0 ? (
          <Card className="p-12 text-center border border-stone-200 bg-white/60 backdrop-blur">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-red-500 to-orange-500 mx-auto mb-6 flex items-center justify-center shadow-lg">
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl sm:text-2xl text-[#1C1917] mb-2 font-semibold">{t('noPlansYet')}</h3>
            <p className="text-base text-stone-600 mb-6">{t('createFirstPlan')}</p>
            <Button data-testid="empty-state-create-button" onClick={() => navigate('/builder')} className="bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md">
              <Plus className="w-4 h-4 mr-2" /> {t('createLessonPlan')}
            </Button>
          </Card>
        ) : (
          <div className="space-y-8">
            {grouped.map(([klass, plans]) => (
              <div key={klass} data-testid={`class-group-${klass}`}>
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs uppercase tracking-widest text-[#C53030] bg-red-50 px-3 py-1 rounded-full font-semibold">{klass}</span>
                  <span className="text-sm text-stone-500">{plans.length} plan{plans.length !== 1 ? 's' : ''}</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-stone-200 to-transparent" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {plans.map((plan) => {
                    const { icon: Icon, bg, fg } = SUBJECT_ICON(plan.subject);
                    return (
                      <div key={plan.lesson_plan_id} className="relative">
                        <div className={`absolute -top-3 -right-3 w-12 h-12 rounded-full bg-gradient-to-br ${bg} flex items-center justify-center shadow-md z-10`}>
                          <Icon className={`w-6 h-6 ${fg}`} />
                        </div>
                        <LessonPlanCard plan={plan} onDelete={handleDelete} onDuplicate={handleDuplicate} />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {worksheets.length > 0 && (
          <section data-testid="dashboard-worksheets-section" className="mt-14">
            <div className="mb-6 flex items-end justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-amber-600 mb-2 font-semibold">Worksheets</p>
                <h3 className="text-2xl sm:text-3xl text-[#1C1917] font-bold tracking-tight">Your Recent Worksheets</h3>
                <p className="text-sm text-stone-600 mt-1">Ready-to-print question papers grounded in your chapter content</p>
              </div>
              <Button data-testid="worksheet-library-btn" variant="outline" onClick={() => navigate('/worksheet-library')} className="border border-stone-200">
                Browse Worksheet Library
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {worksheets.slice(0, 6).map((w) => (
                <Card
                  key={w.worksheet_id}
                  data-testid={`ws-tile-${w.worksheet_id}`}
                  onClick={() => navigate(`/worksheet/${w.worksheet_id}`)}
                  className="group overflow-hidden border border-stone-200 bg-white hover:shadow-xl hover:-translate-y-1 transition-all cursor-pointer"
                >
                  <div className="h-24 bg-gradient-to-br from-amber-500 to-orange-500 relative">
                    <FileText className="absolute right-4 top-4 w-10 h-10 text-white/90" />
                    <div className="absolute bottom-3 left-4 text-white">
                      <p className="text-xs uppercase tracking-wider opacity-90">{w.class_name || 'Class'}</p>
                      <p className="text-sm font-semibold">{w.subject || 'Subject'}</p>
                    </div>
                  </div>
                  <div className="p-5">
                    <p className="font-bold text-[#1C1917] mb-1 line-clamp-2 min-h-[2.5rem]">{w.chapter || 'Untitled'}</p>
                    <div className="flex items-center gap-2 text-xs text-stone-500">
                      <span>{(w.questions?.length || 0)} Q{(w.questions?.length || 0) !== 1 ? 's' : ''}</span><span>·</span>
                      <span>{w.max_marks || 0} marks</span>
                      {w.is_draft ? <><span>·</span><span className="text-amber-600 font-semibold">Draft</span></> : <><span>·</span><span className="text-emerald-600 font-semibold">Final</span></>}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default Dashboard;
